import { inject, Injector } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { map, mergeMap, Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { template } from 'lodash-es';
import { ApiResponseError, apiErrorMessage } from '../api-model';
import { IApiConfig, IApiRequestEndpoint, IApiService, RequestOptionsType } from './api.interface';

const defaultEndpointConfig = {
  path: '',
  auth: true,
  headers: { 'Content-Type': 'application/json' }
}

export class ApiService implements IApiService {
  apiUrl: string;
  authFunction?: (options: RequestOptionsType) => RequestOptionsType;
  authRefreshFunction?: () => Observable<boolean>;
  authFailedFunction?: () => Observable<boolean>;

  protected injector = inject(Injector);
  protected http = inject(HttpClient);
  constructor(
    protected apiConfig?: IApiConfig
  ) {
    if (this.apiConfig) {
      this.apiUrl = this.apiConfig.apiUrl!;

      this.authFunction = this.apiConfig?.authFactory ? this.apiConfig?.authFactory(this.injector) : undefined;
      this.authRefreshFunction = this.apiConfig?.authRefreshFactory ? this.apiConfig?.authRefreshFactory(this.injector) : undefined;
      this.authFailedFunction = this.apiConfig?.authFailedFactory ? this.apiConfig?.authFailedFactory(this.injector) : undefined;
    }
  }

  public endpointConfig(endpointName: string): IApiRequestEndpoint | null {
    const endpoint = this.apiConfig?.endpoints?.[endpointName] ?? null;
    if (typeof endpoint === 'string') {
      return {
        ...defaultEndpointConfig,
        path: endpoint,
      }
    } else if (typeof endpoint === 'object') {
      return {
        ...defaultEndpointConfig,
        ...endpoint
      }
    } else {
      return null;
    }
  }

  public getUrl(endpoint: string, data: { [key: string]: string } = {}): string {
    const endpointConfig = this.endpointConfig(endpoint);

    if (!endpointConfig) {
      throw new Error(`Endpoint ${ endpoint } not found`);
    }
    return this.path2Url(endpointConfig.path, data);
  }

  protected interpolatePath(path: string, data: { [key: string]: string } = {}): string {
    const compiled = template(path);
    return compiled(data);
  }

  protected path2Url(path: string,  options: RequestOptionsType = {}): string {
    path = this.interpolatePath(path, options?.urlParams ?? {});
    return `${ this.apiUrl }/${ path }`;
  }

  public request<T>(endpointName: string, method: string = 'get', options?: RequestOptionsType): Observable<T> {
    const endpointConfig = this.endpointConfig(endpointName);
    if (!endpointConfig) {
      throw new Error(`Endpoint ${ endpointName } not found`);
    }
    const url = this.path2Url(endpointConfig.path, options);

    options = options ?? {};
    let headers = options?.headers
      ? (options.headers instanceof HttpHeaders ? options.headers : new HttpHeaders(options.headers))
      : new HttpHeaders();


    // A FormData body must carry NO Content-Type: only the browser can write
    // `multipart/form-data; boundary=…`, and setting the header by hand silently
    // suppresses the boundary. FTE's `express.json()` then tries to parse the binary
    // part and rejects it with a 400 `SyntaxError: Unexpected token '-', "------WebK"…`
    // — thrown before the logging middleware, so the request leaves no trace in the
    // server log at all, which is what makes this so hard to place from the outside.
    //
    // Guarded here rather than left to each endpoint declaring `headers: {}`, which is
    // how `erpImportRun` and `fileSysUploadPart` avoid it: that convention has to be
    // remembered by every future multipart call site, and it has now been missed once
    // (the SFTP folder browser's upload). A JSON Content-Type on a FormData body is
    // never correct, so nothing is lost by refusing to stamp it. Those two `headers: {}`
    // declarations are now belt-and-braces rather than load-bearing.
    const isFormData = typeof FormData !== 'undefined' && options?.body instanceof FormData;

    for (const key in endpointConfig.headers) {
      if (isFormData && key.toLowerCase() === 'content-type') continue;
      headers = headers.set(key, endpointConfig.headers[key]);
    }
    // A caller that set one explicitly is just as wrong, and for the same reason.
    if (isFormData && headers.has('Content-Type')) {
      headers = headers.delete('Content-Type');
    }
    options['headers'] = headers;

    if (endpointConfig.auth && this.authFunction instanceof Function) {
      options = this.authFunction(options);
    }

    return this.http.request<T>(method, url, options as any).pipe(
      map((response: any) => {
        return response;
      }),
      catchError((error: any) => {
        if (this.authRefreshFunction instanceof Function) {
          switch (error.status) {
            case 401:
              return this.authRefreshFunction().pipe(
                mergeMap((result) => {
                  if (result) {
                    return this.request<T>(endpointName, method, options);
                  } else if (this.authFailedFunction instanceof Function) {
                    return this.authFailedFunction();
                  } else {
                    throw new ApiResponseError(error.message, error.code);
                  }
                })
              );
            default:
              throw new ApiResponseError(
                apiErrorMessage(error.message, error.error),
                error.error.code || error.status,
                error.status,
                error.error
              );
          }
        } else {
          if(error.error) {
            throw new ApiResponseError(
              apiErrorMessage(error.error.message || error.message, error.error),
              error.error.code || error.status,
              error.status,
              error.error
            );
          } else {
            throw new ApiResponseError(error.message, error.status);
          }
        }
      })
    ) as Observable<T>;
  }

  public get<T>(endpointName: string, options?: RequestOptionsType): Observable<T> {
    return this.request(endpointName, 'get', options);
  }

  public post<T>(endpointName: string, options?: RequestOptionsType): Observable<T> {
    return this.request(endpointName, 'post', options);
  }
  public put<T>(endpointName: string, options?: RequestOptionsType): Observable<T> {
    return this.request(endpointName, 'put', options);
  }
  public patch<T>(endpointName: string, options?: RequestOptionsType): Observable<T> {
    return this.request(endpointName, 'patch', options);
  }
  public delete<T>(endpointName: string, options?: RequestOptionsType): Observable<T> {
    return this.request(endpointName, 'delete', options);
  }
}
